import React from 'react';
import { Button, Icon, MiniButton } from '@greystar/designsystem';
import styles from './unitDetails.module.scss';
import { UnitDetailsProps } from './unitDetails.props';
import {
  formatDateToLongMonth,
  backToFpe,
  useFeesAnalytics,
  useAnalytics,
  goBackToComponent,
} from '@greystar/lib';
import { useRouter } from 'next/router';

const UnitDetails = (props: UnitDetailsProps) => {
  const router = useRouter();
  const availableOn = formatDateToLongMonth(props.selectedUnit?.availableOn);
  const bedroomCount =
    props.selectedUnit?.floorplan?.bedroomCount > 0
      ? props.selectedUnit?.floorplan?.bedroomCount
      : 'Studio';

  const { mergeAnalyticsData } = useFeesAnalytics();
  const analytics = useAnalytics();

  const phoneNumber =
    props.property?.contact?.trackingPhoneNumber ?? props.property?.contact?.phoneNumber ?? '';

  const updateData = () => {
    const commonData = {
      room_unit_type: '',
      square_footage: props.selectedUnit?.area,
      bedrooms: props.selectedUnit.floorplan.bedroomCount,
      bathrooms: props.selectedUnit.floorplan.bathroomCount,
      lease_term: props.selectedUnit.leaseLength,
      click_text: phoneNumber,
      preferred_rent: '',
    };

    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_phone',
        action_type: 'phone',
        ...commonData,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_phone', data);
      },
    });
  };

  const handleApply = () => {
    if (!props.property.contact?.leaseOnlineUrl) {
      goBackToComponent(router, 'contact-us');
      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_apply',
          action_type: 'entry',
          click_text: 'Apply',
          destination_url: props.property.contact?.leaseOnlineUrl ?? '',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_apply', data);
        },
      });
    } else {
      props.setIsApplyModalOpen(true);

      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_interstitial',
          action_type: 'interstitial',
          click_text: 'Apply',
          destination_url: '',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_interstitial', data);
        },
      });
    }
  };

  return (
    <section className={styles['unit-details']}>
      <Button
        onClick={() => backToFpe(router, 'fpe-floor-plans')}
        label={'Back to floor plans'}
        variant={'secondary'}
        icon={{
          position: 'left',
          name: 'ArrowLeft',
        }}
      />
      <h2>
        {props.property.name} {props.selectedUnit?.unitNumber}
      </h2>
      {availableOn && (
        <div className={styles['unit-details-availability']}>
          <span>Available</span>
          <span>{formatDateToLongMonth(props.selectedUnit?.availableOn)}</span>
        </div>
      )}
      <div className={styles['unit-details-info']}>
        {props.selectedUnit?.area && (
          <>
            <span>{props.selectedUnit.area}</span>
            <span className={styles['unit-details-info-squarefeet']}> sq. ft.</span>
          </>
        )}
        {bedroomCount && (
          <>
            <Icon iconName={'Bedroom'} size={'md'} />
            <span>{bedroomCount}</span>
          </>
        )}
        {props.selectedUnit?.floorplan?.bathroomCount && (
          <>
            <Icon iconName={'Bathroom'} />
            <span>{props.selectedUnit.floorplan.bathroomCount}</span>
          </>
        )}
      </div>
      <div className={styles['unit-details-cta-row']}>
        <MiniButton
          label="Apply"
          variant="primary"
          icon={
            props.property.contact?.leaseOnlineUrl
              ? { position: 'right', name: 'ExternalLink' }
              : undefined
          }
          isDisabled={false}
          onClick={handleApply}
        />
        <a href={`tel:${phoneNumber}`}>
          <MiniButton
            label={
              props.property?.contact?.trackingPhoneNumber?.toString() ??
              props.property.contact?.phoneNumber?.toString() ??
              ''
            }
            variant="tertiary"
            isDisabled={false}
            onClick={() => updateData()}
          />
        </a>
      </div>
    </section>
  );
};

export default UnitDetails;
